import React, { useState, useEffect } from 'react';
import { FileSearch, Brain, Target, Award, Sparkles } from 'lucide-react';

const STAGES = [
  { icon: FileSearch, label: 'Reading your CV...', hint: 'Extracting skills, experience & qualifications' },
  { icon: Brain, label: 'Analyzing job requirements...', hint: 'Parsing must-have and nice-to-have criteria' },
  { icon: Target, label: 'Matching your skills...', hint: 'Cross-referencing your profile with the role' },
  { icon: Award, label: 'Evaluating experience...', hint: 'Scoring depth and relevance of your background' },
  { icon: Sparkles, label: 'Generating your match report...', hint: 'Putting it all together for you' },
];

const MatchingModal = ({ active }) => {
  const [stageIdx, setStageIdx] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!active) {
      setStageIdx(0);
      setProgress(0);
      return;
    }

    // Animate progress bar smoothly
    const progressTimer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 92) return 92; // cap at 92 so it doesn't look "stuck at 100"
        return prev + 0.4;
      });
    }, 100);

    // Cycle through stages
    const stageTimer = setInterval(() => {
      setStageIdx(prev => (prev < STAGES.length - 1 ? prev + 1 : prev));
    }, 8000);

    return () => {
      clearInterval(progressTimer);
      clearInterval(stageTimer);
    };
  }, [active]);

  if (!active) return null;

  const CurrentIcon = STAGES[stageIdx].icon;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9990,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
      backdropFilter: 'blur(6px)',
    }} data-testid="matching-modal-overlay">
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440,
        padding: '40px 36px 36px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.16)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        textAlign: 'center',
      }} data-testid="matching-modal">

        {/* Animated icon */}
        <div style={{
          width: 72, height: 72, borderRadius: 18,
          background: '#eff6ff', border: '1px solid #dbeafe',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px',
          animation: 'matchPulse 2s ease-in-out infinite',
        }}>
          <CurrentIcon style={{ width: 32, height: 32, color: '#2563eb' }} />
        </div>

        {/* Stage label */}
        <h2 style={{
          fontSize: 20, fontWeight: 700, color: '#0f172a',
          margin: '0 0 6px', lineHeight: 1.3,
          transition: 'opacity 0.3s ease',
        }} data-testid="matching-stage-label">
          {STAGES[stageIdx].label}
        </h2>
        <p style={{
          fontSize: 13, color: '#64748b', margin: '0 0 28px',
          lineHeight: 1.5,
          transition: 'opacity 0.3s ease',
        }}>
          {STAGES[stageIdx].hint}
        </p>

        {/* Progress bar */}
        <div style={{
          width: '100%', height: 6, borderRadius: 3,
          background: '#f1f5f9', overflow: 'hidden',
          marginBottom: 16,
        }}>
          <div style={{
            height: '100%', borderRadius: 3,
            background: 'linear-gradient(90deg, #2563eb, #60a5fa)',
            width: `${Math.min(progress, 100)}%`,
            transition: 'width 0.3s ease',
          }} data-testid="matching-progress-bar" />
        </div>

        {/* Step indicators */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 8,
          marginBottom: 20,
        }}>
          {STAGES.map((_, i) => (
            <div key={i} style={{
              width: 8, height: 8, borderRadius: '50%',
              background: i <= stageIdx ? '#2563eb' : '#e2e8f0',
              transition: 'background 0.3s ease',
            }} />
          ))}
        </div>

        <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
          This usually takes 30-60 seconds
        </p>

        {/* Keyframe animation */}
        <style>{`
          @keyframes matchPulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.06); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default MatchingModal;
